import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { sendPushToTokens } from '../../utils/pushUtils';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

export default function BroadcastPushPage() {
  const { user, role } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);

  // Filter state
  const [locationId, setLocationId] = useState('');
  const [membershipTypeId, setMembershipTypeId] = useState('');
  const [coachId, setCoachId] = useState('');

  // Bericht state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  // Ontvangers preview
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  // Dropdown opties
  const [locations, setLocations] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [coaches, setCoaches] = useState([]);

  // Bevestigingsdialoog
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAdminOrSuper = role === 'admin_coach' || role === 'superadmin';

  // Laad dropdown opties
  useEffect(() => {
    async function loadOptions() {
      try {
        // Locaties
        const { data: locs, error: locErr } = await supabase
          .from('locations')
          .select('id, name')
          .eq('is_active', true)
          .order('name');
        if (locErr) throw locErr;
        setLocations(locs || []);

        // Abonnementstypen
        const { data: types, error: typeErr } = await supabase
          .from('membership_types')
          .select('id, name')
          .eq('is_active', true)
          .order('name');
        if (typeErr) throw typeErr;
        setMembershipTypes(types || []);

        // Coaches (alleen voor admin_coach + superadmin)
        if (isAdminOrSuper) {
          const { data: coachList, error: coachErr } = await supabase
            .from('profiles')
            .select('id, full_name')
            .in('role', ['coach', 'admin_coach', 'superadmin'])
            .is('deleted_at', null)
            .order('full_name');
          if (coachErr) throw coachErr;
          setCoaches(coachList || []);
        }
      } catch (err) {
        console.error('Fout bij laden opties:', err);
        showError(t('broadcastPush.foutLocaties') + err.message);
      }
    }
    loadOptions();
  }, [isAdminOrSuper]);

  // Laad ontvangers bij filter wijziging
  useEffect(() => {
    loadRecipients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, membershipTypeId, coachId]);

  async function loadRecipients() {
    setRecipientsLoading(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('get_broadcast_recipients', {
        p_location_id: locationId || null,
        p_membership_type_id: membershipTypeId || null,
        p_coach_id: coachId || null,
      });
      if (rpcErr) throw rpcErr;
      setRecipients(data || []);
    } catch (err) {
      console.error('Fout bij laden ontvangers:', err);
      showError(t('broadcastPush.foutLaden') + err.message);
      setRecipients([]);
    } finally {
      setRecipientsLoading(false);
    }
  }

  function handleVerstuurClick() {
    if (recipients.length === 0 || !title.trim() || !body.trim()) return;
    setConfirmOpen(true);
  }

  async function handleBevestigVersturen() {
    setConfirmOpen(false);
    setLoading(true);

    try {
      // F4-4.1: rate-limit-check vóór versturen (dagcap per coach).
      const { data: rateLimit, error: rateErr } = await supabase.rpc('check_broadcast_rate_limit');
      if (rateErr) throw rateErr;
      if (rateLimit && rateLimit.allowed === false) {
        showError(
          t('broadcastPush.rateLimitBereikt')
            .replace('{limit}', rateLimit.limit)
            .replace('{used}', rateLimit.used)
        );
        setLoading(false);
        return;
      }

      const clientIds = recipients.map(r => r.client_id);

      const { successCount } = await sendPushToTokens(supabase, user.id, clientIds, 'coach_messages', title, body);

      const noTokenCount = recipients.length - successCount;

      let msg = t('broadcastPush.succesVerstuurd')
        .replace('{sent}', successCount);

      if (noTokenCount > 0) {
        msg += ' ' + t('broadcastPush.succesZonderToken')
          .replace('{skipped}', noTokenCount);
      }

      showSuccess(msg);

      // Reset formulier
      setTitle('');
      setBody('');
    } catch (err) {
      console.error('Fout bij versturen:', err);
      showError(t('broadcastPush.foutVersturen') + err.message);
    } finally {
      setLoading(false);
    }
  }

  const canSubmit = recipients.length > 0 && title.trim() && body.trim() && !loading;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('broadcastPush.title')}</h1>
          <p className="page-subtitle">{t('broadcastPush.subtitle')}</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 className="card-title">{t('broadcastPush.sectionFilters')}</h2>
        </div>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">{t('broadcastPush.filterLocatie')}</label>
            <select
              className="form-input"
              value={locationId}
              onChange={e => setLocationId(e.target.value)}
            >
              <option value="">{t('broadcastPush.filterLocatiePlaceholder')}</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">{t('broadcastPush.filterAbonnementstype')}</label>
            <select
              className="form-input"
              value={membershipTypeId}
              onChange={e => setMembershipTypeId(e.target.value)}
            >
              <option value="">{t('broadcastPush.filterAbonnementstypePlaceholder')}</option>
              {membershipTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {isAdminOrSuper && (
            <div className="form-group">
              <label className="form-label">{t('broadcastPush.filterCoach')}</label>
              <select
                className="form-input"
                value={coachId}
                onChange={e => setCoachId(e.target.value)}
              >
                <option value="">{t('broadcastPush.filterCoachPlaceholder')}</option>
                {coaches.map(coach => (
                  <option key={coach.id} value={coach.id}>
                    {coach.full_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 className="card-title">{t('broadcastPush.previewSection')}</h2>
        </div>
        <div className="card-body">
          {recipientsLoading ? (
            <p style={{ color: 'var(--color-text-secondary)' }}>
              {t('broadcastPush.previewLoading')}
            </p>
          ) : recipients.length > 0 ? (
            <>
              <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>
                {t('broadcastPush.previewOntvangers').replace('{count}', recipients.length)}
              </p>
              <div style={{
                maxHeight: 300,
                overflowY: 'auto',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                padding: 8
              }}>
                {recipients.map((r, idx) => (
                  <div
                    key={r.client_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 8px',
                      borderBottom: idx < recipients.length - 1 ? '1px solid var(--color-border)' : 'none',
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{r.full_name}</span>
                    <span style={{ fontSize: 12, color: r.push_token ? 'var(--color-success)' : 'var(--color-text-secondary)' }}>
                      {r.push_token ? '✓ push token' : t('broadcastPush.geenPushToken')}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--color-text-secondary)' }}>
              {t('broadcastPush.previewGeenOntvangers')}
            </p>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 className="card-title">{t('broadcastPush.sectionBericht')}</h2>
        </div>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">{t('broadcastPush.labelTitel')}</label>
            <input
              type="text"
              className="form-input"
              placeholder={t('broadcastPush.labelTitelPlaceholder')}
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('broadcastPush.labelTekst')}</label>
            <textarea
              className="form-input"
              rows={5}
              placeholder={t('broadcastPush.labelTekstPlaceholder')}
              value={body}
              onChange={e => setBody(e.target.value)}
            />
          </div>
        </div>
      </div>

      <button
        className="btn btn-primary"
        disabled={!canSubmit}
        onClick={handleVerstuurClick}
      >
        {canSubmit
          ? t('broadcastPush.verstuurKnop').replace('{count}', recipients.length)
          : t('broadcastPush.verstuurKnopDisabled')}
      </button>

      {/* Bevestigingsdialoog */}
      {confirmOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setConfirmOpen(false)}
        >
          <div
            className="card"
            style={{ width: 440, maxWidth: '90%' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="card-header">
              <h2 className="card-title">{t('broadcastPush.bevestigingTitel')}</h2>
            </div>
            <div className="card-body">
              <p>{t('broadcastPush.bevestigingTekst').replace('{count}', recipients.length)}</p>
            </div>
            <div className="card-footer" style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmOpen(false)}>
                {t('broadcastPush.bevestigingAnnuleren')}
              </button>
              <button className="btn btn-primary" onClick={handleBevestigVersturen}>
                {t('broadcastPush.bevestigingVersturen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
